import type { Pill, Tone } from "../../model/invoice-view";
import { fxStaleness } from "@talentkontor/domain";
import { money, moneyWhole } from "../../lib/format";

/**
 * Betreiber-Konsole: reine Anzeige-Logik (Agenturen, Abo, Tarifgrenzen, Stripe-Ereignisse, Support-Zugang).
 * Datenquelle ist platform_org_overview (0033): je Agentur nur Zählwerte und Zeitpunkte, keine Inhalte der Agentur.
 */

export type OrgStatus = "trial" | "active" | "past_due" | "suspended" | "cancelled";
export type SubStatus = "trialing" | "active" | "past_due" | "cancelled";

export interface OrgOverviewRow {
  org_id: string;
  name: string;
  slug: string;
  status: OrgStatus;
  trial_ends_at: string | null;
  created_at: string;
  plan_key: string | null;
  plan_name: string | null;
  subscription_status: SubStatus | null;
  billing_interval: "month" | "year" | null;
  current_period_end: string | null;
  price_month_cents: number | null;
  price_year_cents: number | null;
  talents: number;
  seats: number;
  contracts_this_month: number;
  storage_bytes: number;
  last_activity_at: string | null;
  chef_emails: string[];
}
export interface PlanRow {
  key: string;
  name: string;
  price_month_cents: number;
  price_year_cents: number;
  active: boolean;
  sort_order: number;
}
export interface EntitlementRow {
  plan_key: string;
  key: string;
  value: number;
}
export interface SubscriptionRow {
  org_id: string;
  plan_key: string;
  status: SubStatus;
  billing_interval: "month" | "year";
  current_period_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  updated_at: string;
}
export interface BillingEventRow {
  stripe_event_id: string;
  type: string;
  org_id: string | null;
  received_at: string;
  outcome: string;
}
export interface GrantRow {
  id: string;
  org_id: string;
  platform_admin_id: string;
  reason: string;
  approved_by: string | null;
  starts_at: string;
  expires_at: string;
  revoked_at: string | null;
  created_at: string;
}
export type SupportEvent = "session_opened" | "revoked_by_agency" | "revoked_by_platform" | "revoked_by_system";
export interface SupportLogRow {
  at: string;
  org_id: string;
  grant_id: string | null;
  event: SupportEvent | string;
}

// ── Anzeige ─────────────────────────────────────────────────────────────────────────────────────────

const SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
export const pill = (label: string, tone: Tone): Pill => ({ label, tone, symbol: SYMBOL[tone] });

/** Frist-Ampel eines Löschantrags (Art. 12 Abs. 3 DSGVO: ein Monat). */
export function deletionPill(row: { processed_at: string | null; due_at: string; outcome: string | null }, nowMs: number): Pill {
  if (row.processed_at) return pill(row.outcome === "anonymized" ? "Erledigt · unkenntlich gemacht" : "Erledigt · gelöscht", "success");
  const days = Math.ceil((Date.parse(row.due_at) - nowMs) / 86400000);
  if (days < 0) return pill(`Frist seit ${-days} ${days === -1 ? "Tag" : "Tagen"} überschritten`, "error");
  if (days <= 7) return pill(`noch ${days} ${days === 1 ? "Tag" : "Tage"}`, "warning");
  return pill(`noch ${days} Tage`, "info");
}

/** Lebenszyklus-Protokoll in Klartext. */
export function lifecycleActionLabel(action: string): string {
  return ({
    cancelled: "Kündigung wirksam", announced: "Schritt angekündigt", reduced: "Auf Belege reduziert", purged: "Endgültig gelöscht",
    error: "Fehler", storage_pending: "Dateien noch zu löschen", account_deleted: "Konto gelöscht",
    // 0092: Laufzeitende eines von Hand freigeschalteten Abos (RECHTE-R3-5)
    subscription_expiring: "Abo-Laufzeit endet bald (Hinweis an die Admins)", subscription_expired: "Abo-Laufzeit abgelaufen, Nachfrist läuft",
    subscription_suspended: "Gesperrt: Abo-Laufzeit abgelaufen",
  } as Record<string, string>)[action] ?? action;
}

/**
 * Wertebereiche der Produkt-Standards (gleich app.platform_default_problem in 0057; die Datenbank prüft selbst) und
 * Schlüssel, deren Änderung Löschungen verschiebt (mit Rückfrage).
 */
export const DEFAULT_RANGES: Record<string, string> = {
  trial_days: "1 bis 60 Tage", max_open_trials: "1 bis 10", read_only_min_days: "90 bis 730 Tage", purge_notice_days: "30 bis 180 Tage",
  retention_years: "10 bis 30 Jahre (Rückfall ohne Landeswert)", account_deletion_days: "1 bis 30 Tage", due_soon_days: "0 bis 60 Tage",
  invitation_days: "1 bis 60 Tage", signing_link_days: "1 bis 90 Tage", requested_urgent_days: "1 bis 90 Tage", stale_draft_days: "1 bis 90 Tage",
  talent_answer_days: "1 bis 60 Tage", payment_check_cooldown_days: "1 bis 30 Tage", still_overdue_recheck_days: "1 bis 30 Tage",
  chat_retract_hours: "0 bis 168 Stunden", free_revisions: "0 bis 10", min_line_description_chars: "0 bis 200 Zeichen", budget_deviation_percent: "0 bis 100 %",
  fee_mode: "cumulative oder highest", revenue_start: "status oder contract_signed", talent_payout_mode: "fixed_terms oder after_brand_payment",
  trial_plan: "Schlüssel eines angebotenen Tarifs", support_email: "E-Mail-Adresse oder leer",
  retention_by_country: "je Land books und letters in Jahren; nie unter DE 8/6, AT 7, CH 10",
  legal_versions: "agency_terms, user_terms, privacy, dpa als Datum JJJJ-MM-TT",
  // 0092: Laufzeitende eines Abos ohne Stripe (der Tageslauf begrenzt die Werte)
  subscription_notice_days: "0 bis 60 Tage", subscription_grace_days: "0 bis 90 Tage",
  // 0101: Betriebsbericht und Sofortwarnung (app.platform_default_problem_more)
  ops_mail_language: "de oder en", ops_time_zone: "Zeitzone wie Europe/Berlin", ops_alert_failed_mails: "1 bis 1000 Mails je Stunde",
  ops_alert_consecutive: "1 bis 20 Fehlschläge hintereinander", ops_alert_cooldown_hours: "1 bis 72 Stunden",
  // 0104: Zustellbarkeit (app.platform_default_problem_more)
  mail_hourly_limit: "10 bis 10000 Mails je Agentur und Stunde", mail_hourly_limit_trial: "1 bis 10000 Mails je Stunde in der Testphase",
  mail_bounce_alert_percent: "1 bis 50 %", mail_bounce_min_mails: "1 bis 1000 Mails in 7 Tagen",
  // 0182: B2B-Bestätigung, Datenschutzadresse, Speicherdauer der Supporttickets (vorbereitet, null = keine Löschung)
  b2b_declaration_version: "Datum JJJJ-MM-TT (gleichzeitig mit dem Wortlaut in der App ändern)", privacy_email: "E-Mail-Adresse oder leer (Supportadresse)",
  support_ticket_retention_days: "null (keine automatische Löschung) oder 1 bis 3650 Tage; ein Löschlauf fehlt noch",
};

/** Laufzeitende in der Vergangenheit (RECHTE-R3-5): die Konsole zeigt „abgelaufen“ statt „nächste Abrechnung“ bzw. „Laufzeit bis“. */
export function laufzeitAbgelaufen(end: string | null | undefined, nowMs: number): boolean {
  return !!end && Date.parse(end) < nowMs;
}
export const PURGE_KEYS = ["read_only_min_days", "purge_notice_days", "retention_years", "retention_by_country"];
/** Klartext für die Rückfrage bei löschrelevanten Standards. */
export function purgeImpact(key: string, value: unknown, nowMs: number): string {
  const n = typeof value === "number" ? value : Number(value);
  const day = (d: number) => new Intl.DateTimeFormat("de-DE", { dateStyle: "long", timeZone: "UTC" }).format(new Date(nowMs + d * 86400000));
  switch (key) {
    case "read_only_min_days":
      return Number.isFinite(n) ? `Eine heute gekündigte Agentur kann danach bis mindestens ${day(Math.max(n, 90))} exportieren (Export-Phase, Portal gesperrt); erst dann werden ihre Daten (nach Ankündigung) reduziert oder gelöscht.` : "";
    case "purge_notice_days":
      return Number.isFinite(n) ? `Reduktion und Löschung werden danach ${Math.max(n, 30)} Tage vorher angekündigt.` : "";
    case "retention_years":
    case "retention_by_country":
      return "Aufbewahrte Belege gekündigter Agenturen werden nach der neuen Frist gelöscht; ist sie kürzer, kann die Löschung früher fällig werden (immer mit Ankündigung).";
    default:
      return "";
  }
}

export const ORG_STATUS: readonly { key: OrgStatus; label: string; tone: Tone }[] = [
  { key: "active", label: "Aktiv", tone: "success" },
  { key: "trial", label: "Testphase", tone: "info" },
  { key: "past_due", label: "Zahlung offen", tone: "warning" },
  { key: "suspended", label: "Gesperrt", tone: "error" },
  { key: "cancelled", label: "Gekündigt", tone: "neutral" },
];
export function orgStatusPill(status: string): Pill {
  const s = ORG_STATUS.find((x) => x.key === status);
  return s ? pill(s.label, s.tone) : pill(status, "neutral");
}
const SUB_LABEL: Record<SubStatus, string> = { trialing: "Testphase", active: "Aktiv", past_due: "Zahlung offen", cancelled: "Gekündigt" };
export function subscriptionLabel(status: string | null): string {
  return status ? SUB_LABEL[status as SubStatus] ?? status : "Kein Abo";
}

/** Zeitpunkt für den Betrieb immer in UTC, wie im Zugriffsprotokoll des Pakets: „24.09.2026, 10:00 UTC“. */
export function utcDateTime(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return `${new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }).format(d)} UTC`;
}
export function utcDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" }).format(d);
}

/**
 * 0441 (W-3): Anbieterwechsel in der Zeit des Betriebs (Europe/Berlin), Anzeige wie Eingabe. Stand: „01.12.2026, 23:59 Uhr“.
 */
export const BETRIEB_ZONE = "Europe/Berlin";
export function berlinDateTime(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return `${new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: BETRIEB_ZONE }).format(d)} Uhr`;
}
export function berlinDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: BETRIEB_ZONE }).format(d);
}
/** Abstand von Europe/Berlin zu UTC an diesem Kalendertag um die Uhrzeit hh:mm, als „+01:00“ bzw. „+02:00“ */
function berlinOffset(day: string, hhmm: string): string {
  // Ortszeit als UTC gelesen, dann zurückgerechnet: die Differenz ist der Abstand zu dieser Uhrzeit (auch an Umstellungstagen)
  const alsUtc = new Date(`${day}T${hhmm}:00Z`);
  const teile = new Intl.DateTimeFormat("en-GB", { timeZone: BETRIEB_ZONE, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })
    .formatToParts(alsUtc).reduce<Record<string, string>>((a, p) => ({ ...a, [p.type]: p.value }), {});
  const ort = Date.UTC(Number(teile.year), Number(teile.month) - 1, Number(teile.day), Number(teile.hour), Number(teile.minute));
  const min = Math.round((ort - alsUtc.getTime()) / 60000);
  const h = String(Math.floor(Math.abs(min) / 60)).padStart(2, "0");
  const m = String(Math.abs(min) % 60).padStart(2, "0");
  return `${min < 0 ? "-" : "+"}${h}:${m}`;
}
/** Kalendertag (YYYY-MM-DD) als Zeitpunkt in Europe/Berlin: Tagesende 23:59:59 bzw. Mittag */
export function berlinTagesende(day: string): string {
  return `${day}T23:59:59${berlinOffset(day, "23:59")}`;
}
export function berlinMittag(day: string): string {
  return `${day}T12:00:00${berlinOffset(day, "12:00")}`;
}

export function countLabel(n: number): string {
  return new Intl.NumberFormat("de-DE").format(n);
}
/** 2048 → „2 KB“, 5_368_709_120 → „5 GB“ (1024er Stufen wie die Tarifgrenze storage_gb). */
export function bytesLabel(bytes: number): string {
  const units = ["Byte", "KB", "MB", "GB", "TB"];
  let v = Math.max(0, bytes);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const digits = i === 0 || v >= 10 ? 0 : 1;
  return `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(v)} ${units[i]}`;
}

// ── Abo und Umsatz ───────────────────────────────────────────────────────────────────────────────────

/** Monatlicher Betrag eines Abos: Jahresabos anteilig (Jahrespreis / 12). */
export function monthlyCents(row: Pick<OrgOverviewRow, "billing_interval" | "price_month_cents" | "price_year_cents">): number | null {
  if (row.billing_interval === "year") return row.price_year_cents === null ? null : Math.round(row.price_year_cents / 12);
  return row.price_month_cents;
}
/** Zahlende Abos: aktiv oder mit offener Zahlung (vertraglich weiter geschuldet). Testphasen und gekündigte zählen nicht. */
export function isPaying(row: Pick<OrgOverviewRow, "subscription_status">): boolean {
  return row.subscription_status === "active" || row.subscription_status === "past_due";
}
export function mrrCents(rows: readonly OrgOverviewRow[]): number {
  return rows.filter(isPaying).reduce((sum, r) => sum + (monthlyCents(r) ?? 0), 0);
}
/** „179 € monatlich“ bzw. „1.790 € jährlich“ wie in der Agenturenliste des Pakets. */
export function priceLabel(row: Pick<OrgOverviewRow, "billing_interval" | "price_month_cents" | "price_year_cents" | "plan_key">): string {
  if (!row.plan_key) return "Ohne Tarif";
  if (row.billing_interval === "year") return row.price_year_cents === null ? "–" : `${moneyWhole(row.price_year_cents)} jährlich`;
  return row.price_month_cents === null ? "–" : `${moneyWhole(row.price_month_cents)} monatlich`;
}
export function intervalLabel(interval: string | null): string {
  return interval === "year" ? "Jährlich" : interval === "month" ? "Monatlich" : "–";
}

// ── Testphase ────────────────────────────────────────────────────────────────────────────────────────

const DAY = 86_400_000;
/**
 * Angefangene Tage bis zum Ende der Testphase, nur für Agenturen im Status „Testphase“: 1 = endet innerhalb von 24 Stunden,
 * negativ = abgelaufen (−1 innerhalb des ersten Tages danach). Null gibt es nicht, damit „heute“ nie mehrdeutig ist.
 */
export function trialDaysLeft(row: Pick<OrgOverviewRow, "status" | "trial_ends_at">, nowMs: number): number | null {
  if (row.status !== "trial" || !row.trial_ends_at) return null;
  const end = Date.parse(row.trial_ends_at);
  if (Number.isNaN(end)) return null;
  const diff = end - nowMs;
  return diff > 0 ? Math.ceil(diff / DAY) : -Math.max(1, Math.ceil(-diff / DAY));
}
export function trialLabel(days: number | null): string | null {
  if (days === null) return null;
  if (days < 0) return "abgelaufen";
  return days === 1 ? "endet innerhalb von 24 Stunden" : `endet in ${days} Tagen`;
}

// ── Liste: Kennzahlen, Filter ────────────────────────────────────────────────────────────────────────

export type StatusFilter = "alle" | OrgStatus;
export type TrialFilter = "alle" | "7" | "14" | "abgelaufen";
export interface ListFilter {
  search: string;
  status: StatusFilter;
  plan: string;
  trial: TrialFilter;
}
export const TRIAL_FILTERS: readonly { key: TrialFilter; label: string }[] = [
  { key: "alle", label: "Testphase endet: egal" },
  { key: "7", label: "Testphase endet in 7 Tagen" },
  { key: "14", label: "Testphase endet in 14 Tagen" },
  { key: "abgelaufen", label: "Testphase abgelaufen" },
];

export function matchesTrial(row: OrgOverviewRow, trial: TrialFilter, nowMs: number): boolean {
  if (trial === "alle") return true;
  const d = trialDaysLeft(row, nowMs);
  if (d === null) return false;
  if (trial === "abgelaufen") return d < 0;
  return d > 0 && d <= Number(trial);
}
export function matchesList(row: OrgOverviewRow, f: ListFilter, nowMs: number): boolean {
  const q = f.search.trim().toLowerCase();
  if (q && !`${row.name} ${row.slug} ${row.plan_name ?? ""} ${row.chef_emails.join(" ")}`.toLowerCase().includes(q)) return false;
  if (f.status !== "alle" && row.status !== f.status) return false;
  if (f.plan !== "alle" && (row.plan_key ?? "ohne") !== f.plan) return false;
  return matchesTrial(row, f.trial, nowMs);
}

export interface ListKpis {
  total: number;
  active: number;
  trials: number;
  trialsSoon: number;
  paying: number;
  mrrCents: number;
}
export function listKpis(rows: readonly OrgOverviewRow[], nowMs: number): ListKpis {
  return {
    total: rows.length,
    active: rows.filter((r) => r.status === "active").length,
    trials: rows.filter((r) => r.status === "trial").length,
    trialsSoon: rows.filter((r) => matchesTrial(r, "7", nowMs)).length,
    paying: rows.filter(isPaying).length,
    mrrCents: mrrCents(rows),
  };
}

// ── Tarifgrenzen ─────────────────────────────────────────────────────────────────────────────────────

export function limitsOf(planKey: string | null, entitlements: readonly EntitlementRow[]): Record<string, number> {
  const out: Record<string, number> = {};
  if (!planKey) return out;
  for (const e of entitlements) if (e.plan_key === planKey) out[e.key] = e.value;
  return out;
}

const GB = 1024 ** 3;
export interface UsageRow {
  key: "talents" | "seats" | "contracts" | "storage";
  label: string;
  used: number;
  usedLabel: string;
  /** null: keine Grenze hinterlegt (ohne Tarif); -1: unbegrenzt */
  max: number | null;
  maxLabel: string;
  /** Anteil 0…100 für den Balken, null ohne Grenze */
  pct: number | null;
  state: "ok" | "near" | "over" | "unlimited" | "none";
}
function usage(key: UsageRow["key"], label: string, used: number, limit: number | undefined, fmt: (n: number) => string, scale = 1): UsageRow {
  if (limit === undefined) return { key, label, used, usedLabel: fmt(used), max: null, maxLabel: "keine Grenze hinterlegt", pct: null, state: "none" };
  if (limit < 0) return { key, label, used, usedLabel: fmt(used), max: -1, maxLabel: "unbegrenzt", pct: null, state: "unlimited" };
  const max = limit * scale;
  const pct = max === 0 ? (used > 0 ? 100 : 0) : Math.min(100, (used / max) * 100);
  const state = used > max ? "over" : max > 0 && used / max >= 0.9 ? "near" : "ok";
  return { key, label, used, usedLabel: fmt(used), max, maxLabel: fmt(max), pct, state };
}
/** Nutzung gegen die Grenzen des Tarifs, gezählt wie org_entitlements und die Tarif-Wächter. */
export function usageRows(row: OrgOverviewRow, limits: Record<string, number>): UsageRow[] {
  return [
    usage("talents", "Creator (ohne archivierte)", row.talents, limits.max_talents, countLabel),
    usage("seats", "Team-Plätze", row.seats, limits.max_seats, countLabel),
    usage("contracts", "Verträge in diesem Monat (versendet)", row.contracts_this_month, limits.max_contracts_per_month, countLabel),
    usage("storage", "Speicher der Anhänge", row.storage_bytes, limits.storage_gb, bytesLabel, GB),
  ];
}
/** Überschreitungen beim Freischalten eines Tarifs (wie app.plan_overage, 0077): aktive Creator und Team-Plätze. */
export function planOverage(row: OrgOverviewRow, planKey: string, entitlements: readonly EntitlementRow[]): string[] {
  return usageRows(row, limitsOf(planKey, entitlements))
    .filter((u) => (u.key === "talents" || u.key === "seats") && u.state === "over")
    .map((u) => `${u.key === "talents" ? "Creator" : "Team-Plätze"}: ${u.usedLabel} aktiv, der Tarif erlaubt ${u.maxLabel}`);
}
export function usagePill(u: UsageRow): Pill | null {
  if (u.state === "over") return pill("Über der Grenze", "error");
  if (u.state === "near") return pill("Fast ausgeschöpft", "warning");
  return null;
}

/** Module des Katalogs in der Reihenfolge der Tarifstaffel (0099): Zusammenarbeit & Wachstum, dann Finanzen & Automatik. */
export const MODULES: readonly { key: string; label: string }[] = [
  { key: "module_rights", label: "Team-Rechte und Sichtbereiche" },
  { key: "module_pipeline", label: "Anfragen und Angebote" },
  { key: "module_media_kit", label: "Media-Kits und Pitch-Listen" },
  { key: "module_brand_review", label: "Freigabe-Link an die Marke" },
  { key: "module_insights", label: "Insights-Freigaben" },
  { key: "module_sender_domain", label: "Eigene Absenderdomain" },
  { key: "module_mail_unbranded", label: "Mails ohne CreatorOffice-Hinweis" },
  { key: "module_dunning_custom", label: "Eigene Mahngebühren und Mahntexte" },
  { key: "module_payouts", label: "Auszahlungsläufe und Gutschriften" },
  { key: "module_accounting_export", label: "DATEV- und BMD-Export" },
  { key: "module_bank_sync", label: "Kontoauszug-Import und Liquidität" },
  { key: "module_whatsapp", label: "WhatsApp-Modul" },
  { key: "module_custom_domain", label: "Eigene Domain" },
];
export function moduleStates(limits: Record<string, number>): { key: string; label: string; on: boolean }[] {
  return MODULES.map((m) => ({ ...m, on: (limits[m.key] ?? 0) >= 1 }));
}

// ── Tarifänderung vorbereiten (nur Vorschau: umgestellt wird in Stripe) ─────────────────────────────

export interface PlanPreview {
  same: boolean;
  interval: "month" | "year";
  priceFrom: number | null;
  priceTo: number;
  diff: number | null;
  limits: { label: string; used: string; from: string; to: string; over: boolean }[];
  modules: { label: string; from: boolean; to: boolean }[];
  warnings: string[];
}
export function planPreview(row: OrgOverviewRow, target: PlanRow, entitlements: readonly EntitlementRow[]): PlanPreview {
  const interval = row.billing_interval === "year" ? "year" : "month";
  const priceFrom = row.plan_key ? (interval === "year" ? row.price_year_cents : row.price_month_cents) : null;
  const priceTo = interval === "year" ? target.price_year_cents : target.price_month_cents;
  const from = usageRows(row, limitsOf(row.plan_key, entitlements));
  const to = usageRows(row, limitsOf(target.key, entitlements));
  const limits = from.map((f, i) => {
    const t = to[i]!;
    return { label: f.label, used: f.usedLabel, from: f.maxLabel, to: t.maxLabel, over: t.state === "over" };
  });
  const toModules = moduleStates(limitsOf(target.key, entitlements));
  const modules = moduleStates(limitsOf(row.plan_key, entitlements)).map((m, i) => ({ label: m.label, from: m.on, to: toModules[i]!.on }));
  const warnings: string[] = [];
  for (const [i, t] of to.entries()) {
    if (t.state !== "over" || t.max === null) continue;
    const excess = t.used - t.max;
    if (t.key === "talents") warnings.push(`${countLabel(excess)} Creator über der neuen Grenze: Bestehende bleiben, neue lassen sich erst nach dem Archivieren anlegen.`);
    else if (t.key === "seats") warnings.push(`${excess === 1 ? "1 Team-Platz" : `${countLabel(excess)} Team-Plätze`} über der neuen Grenze: Bestehende bleiben, neue Mitglieder lassen sich nicht einladen.`);
    else if (t.key === "contracts") warnings.push("In diesem Monat sind schon mehr Verträge versendet, als der neue Tarif erlaubt: Weitere lassen sich erst im nächsten Monat senden.");
    else warnings.push(`Die Anhänge belegen mehr Speicher, als der neue Tarif vorsieht (${from[i]!.usedLabel} von ${t.maxLabel}).`);
  }
  for (const m of modules) {
    if (m.from && !m.to && m.label === "Insights-Freigaben") warnings.push("Ohne Insights sieht das Team keine Instagram-Kennzahlen mehr und kann keine neuen Freigaben an Marken anlegen.");
    else if (m.from && !m.to) warnings.push(`${m.label} entfällt mit dem neuen Tarif.`);
  }
  return { same: row.plan_key === target.key, interval, priceFrom, priceTo, diff: priceFrom === null ? null : priceTo - priceFrom, limits, modules, warnings };
}
export function signedMoney(cents: number): string {
  if (cents === 0) return "gleich";
  return `${cents > 0 ? "+" : "−"}${money(Math.abs(cents))}`;
}

// ── Stripe-Ereignisse ────────────────────────────────────────────────────────────────────────────────

const EVENT_LABEL: Record<string, string> = {
  "checkout.session.completed": "Bezahlvorgang abgeschlossen",
  "checkout.session.async_payment_succeeded": "Lastschrift eingegangen",
  "checkout.session.async_payment_failed": "Lastschrift gescheitert",
  "customer.subscription.created": "Abo angelegt",
  "customer.subscription.updated": "Abo geändert",
  "customer.subscription.deleted": "Abo beendet",
  "customer.subscription.trial_will_end": "Testphase endet bald",
  "invoice.paid": "Rechnung bezahlt",
  "invoice.payment_succeeded": "Rechnung bezahlt",
  "invoice.payment_failed": "Zahlung fehlgeschlagen",
  "invoice.finalized": "Rechnung erstellt",
};
export function billingEventLabel(type: string): string {
  return EVENT_LABEL[type] ?? type;
}
export function billingOutcomePill(outcome: string): Pill {
  switch (outcome) {
    case "applied": return pill("Übernommen", "success");
    case "duplicate": return pill("Doppelt, übersprungen", "neutral");
    case "ignored": return pill("Ohne Wirkung", "neutral");
    case "unknown_org": return pill("Agentur nicht zugeordnet", "error");
    case "pending": return pill("In Bearbeitung", "warning");
    case "review": return pill("Nicht übernommen · prüfen", "warning");
    case "stale": return pill("Veraltet, übersprungen", "neutral");
    case "awaiting_payment": return pill("Zahlung ausstehend (Lastschrift)", "warning");
    case "payment_failed": return pill("Zahlung gescheitert, nicht übernommen", "error");
    default: return pill(outcome, "neutral");
  }
}
/** „Letzte Rechnung: bezahlt“ aus dem jüngsten Rechnungsereignis von Stripe. */
export function lastInvoiceText(events: readonly BillingEventRow[]): string {
  const last = [...events].filter((e) => e.type.startsWith("invoice.")).sort((a, b) => b.received_at.localeCompare(a.received_at))[0];
  if (!last) return "Noch keine Rechnung über Stripe";
  if (last.type === "invoice.paid" || last.type === "invoice.payment_succeeded") return `bezahlt (${utcDate(last.received_at)})`;
  if (last.type === "invoice.payment_failed") return `Zahlung fehlgeschlagen (${utcDate(last.received_at)})`;
  return `${billingEventLabel(last.type)} (${utcDate(last.received_at)})`;
}

// ── Support-Zugang ───────────────────────────────────────────────────────────────────────────────────

export type GrantState = "active" | "planned" | "expired" | "revoked";
/** Beginn setzt die Datenbank (now()); eine vorgehende Uhr im Browser macht einen neuen Zugang nicht zu einem „späteren“. */
const CLOCK_SKEW = 5 * 60_000;
export function grantState(g: Pick<GrantRow, "starts_at" | "expires_at" | "revoked_at">, nowMs: number): GrantState {
  if (g.revoked_at) return "revoked";
  if (Date.parse(g.expires_at) <= nowMs) return "expired";
  if (Date.parse(g.starts_at) > nowMs + CLOCK_SKEW) return "planned";
  return "active";
}
export function grantPill(state: GrantState): Pill {
  switch (state) {
    case "active": return pill("Aktiv · nur Lesen", "success");
    case "planned": return pill("Beginnt später", "info");
    case "expired": return pill("Abgelaufen", "neutral");
    case "revoked": return pill("Beendet", "neutral");
  }
}

/** Dauer des Zugangs. Die Datenbank erlaubt höchstens 72 Stunden ab Beginn; 48 Stunden lassen Spielraum für abweichende Uhren. */
export const DURATIONS: readonly { minutes: number; label: string }[] = [
  { minutes: 15, label: "15 Minuten" },
  { minutes: 30, label: "30 Minuten" },
  { minutes: 60, label: "60 Minuten" },
  { minutes: 240, label: "4 Stunden" },
  { minutes: 1440, label: "24 Stunden" },
  { minutes: 2880, label: "48 Stunden" },
];
/** Längste wählbare Dauer; der Hinweis unter dem Formular nennt sie (nicht die 72 Stunden der Datenbankgrenze; SICHT-OEFFENTLICH-BETRIEB-14). */
export const MAX_DURATION_LABEL = DURATIONS[DURATIONS.length - 1]!.label;

/** Ticket und Begründung teilen sich die Spalte reason („SUP-2026-012 · Begründung“), eine eigene Spalte gibt es nicht. */
/** Ticketkennung: ohne Leerzeichen, 2 bis 40 Zeichen, mindestens eine Ziffer (sonst wäre jedes erste Wort ein Ticket). */
const TICKET = /^(?=[^ ]*\d)[A-Za-z0-9][A-Za-z0-9_./-]{1,39}$/;
const SEP = " · ";
export function composeReason(ticket: string, text: string): string {
  const t = ticket.trim();
  const body = text.trim();
  return t ? `${t}${SEP}${body}` : body;
}
export function splitReason(reason: string): { ticket: string | null; text: string } {
  const i = reason.indexOf(SEP);
  if (i > 0) {
    const head = reason.slice(0, i);
    if (TICKET.test(head)) return { ticket: head, text: reason.slice(i + SEP.length) };
  }
  return { ticket: null, text: reason };
}

export interface GrantInput {
  orgId: string;
  ticket: string;
  reason: string;
  minutes: number;
}
export type GrantErrors = Partial<Record<"orgId" | "ticket" | "reason" | "minutes", string>>;
export function validateGrant(input: GrantInput, orgIds: readonly string[]): GrantErrors {
  const e: GrantErrors = {};
  if (!orgIds.includes(input.orgId)) e.orgId = "Bitte eine Agentur wählen.";
  if (input.ticket.trim() && !TICKET.test(input.ticket.trim())) e.ticket = "Ticket bitte ohne Leerzeichen, 2 bis 40 Zeichen, mit mindestens einer Ziffer (z. B. SUP-2026-012).";
  if (input.reason.trim().length < 10) e.reason = "Bitte eine Begründung mit mindestens 10 Zeichen angeben.";
  if (!DURATIONS.some((d) => d.minutes === input.minutes)) e.minutes = "Bitte eine Dauer wählen.";
  return e;
}

export interface ProtocolEntry {
  key: string;
  at: string;
  orgId: string;
  text: string;
}
/**
 * Zugriffsprotokoll aus den eigenen Zugängen und platform_support_log: geöffnet, Sitzung begonnen,
 * vorzeitig beendet (durch Agentur oder Support), abgelaufen. Neueste zuerst.
 */
export function protocolEntries(grants: readonly GrantRow[], log: readonly SupportLogRow[], nowMs: number): ProtocolEntry[] {
  const out: ProtocolEntry[] = [];
  for (const g of grants) {
    const { ticket } = splitReason(g.reason);
    out.push({ key: `${g.id}-auf`, at: g.created_at, orgId: g.org_id, text: `Zugang geöffnet${ticket ? ` · Ticket ${ticket}` : ""} · nur Lesen bis ${utcDateTime(g.expires_at)}` });
    const revokedLogged = log.some((l) => l.grant_id === g.id && String(l.event).startsWith("revoked_by_"));
    if (g.revoked_at && !revokedLogged) out.push({ key: `${g.id}-ende`, at: g.revoked_at, orgId: g.org_id, text: "Zugang beendet" });
    if (!g.revoked_at && Date.parse(g.expires_at) <= nowMs) out.push({ key: `${g.id}-ablauf`, at: g.expires_at, orgId: g.org_id, text: "Zugang abgelaufen, automatisch beendet" });
  }
  log.forEach((l, i) => {
    const text = l.event === "session_opened" ? "Sitzung begonnen"
      : l.event === "revoked_by_agency" ? "Zugang beendet durch die Agentur"
      : l.event === "revoked_by_platform" ? "Zugang beendet durch den Support"
      : l.event === "revoked_by_system" ? "Zugang beendet durch das System"
      : String(l.event);
    out.push({ key: `log-${i}-${l.at}`, at: l.at, orgId: l.org_id, text });
  });
  return out.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
}

// ── Technische Diagnose (nur mit aktivem Zugang): Stand der Einrichtung, ohne Werte ──────────────────

const STEP_LABEL: Record<string, string> = {
  company: "Firma & Recht", bank: "Bankverbindung", branding: "Branding & Sprache", terms: "Konditionen", dunning: "Mahnwesen", invoicing: "Rechnungen",
};
export interface SetupStatus {
  complete: boolean;
  missing: { step: string; key: string }[];
}
export function parseSetup(raw: unknown): SetupStatus | null {
  const v = typeof raw === "string" ? (() => { try { return JSON.parse(raw) as unknown; } catch { return null; } })() : raw;
  if (!v || typeof v !== "object") return null;
  const o = v as { complete?: unknown; missing?: unknown };
  const missing = Array.isArray(o.missing)
    ? o.missing.filter((m): m is { step: string; key: string } => !!m && typeof m === "object" && typeof (m as { step?: unknown }).step === "string" && typeof (m as { key?: unknown }).key === "string")
    : [];
  return { complete: o.complete === true, missing };
}
/** Offene Schritte der Einrichtung mit Anzahl fehlender Angaben (nur Schlüssel, nie Werte). */
export function setupSteps(s: SetupStatus): { step: string; label: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const m of s.missing) counts.set(m.step, (counts.get(m.step) ?? 0) + 1);
  return [...counts.entries()].map(([step, count]) => ({ step, label: STEP_LABEL[step] ?? step, count }));
}

/**
 * Wechselkurse (PARITAET-1): Warnung für den Betrieb, wenn der tägliche Abruf der EZB-Kurse (Serverfunktion fx-rates) nicht
 * läuft, also der letzte Kurs älter als drei Werktage ist oder es gar keinen gibt; sonst null.
 */
export function fxWarning(lastValidOn: string | null, today: string): string | null {
  const s = fxStaleness(lastValidOn, today);
  if (!s.stale) return null;
  const folge = "Beträge in Fremdwährung fehlen dann in Umsätzen, Forderungen und Pipeline der Agenturen. Bitte die Serverfunktion fx-rates und ihren Zeitplan prüfen (docs/EINSPIELPLAN.md, Abschnitt 6).";
  return lastValidOn
    ? `Wechselkurse veraltet: Der letzte EZB-Kurs ist vom ${utcDate(`${lastValidOn.slice(0, 10)}T12:00:00Z`)} (${s.businessDays} Werktage alt). ${folge}`
    : `Keine Wechselkurse hinterlegt: Der tägliche Abruf der EZB-Kurse läuft nicht. ${folge}`;
}

/**
 * Basiszinssätze (SICHT-OEFFENTLICH-BETRIEB-19): Warnungen für den Betrieb, wenn für ein Land mit Regel „Basiszins plus
 * Aufschlag“ kein Satz hinterlegt ist (Verzugszinsen fallen dann ganz weg, Warnung NO_BASE_RATE in der Mahnung) oder der
 * jüngste Satz vor dem Beginn des laufenden Halbjahres gilt (neue Sätze zum 1. Januar und 1. Juli; die Agenturen rechneten
 * sonst still mit dem alten weiter). Leere Liste: alles gepflegt.
 */
export function baseRateWarnings(rules: readonly { country: string; mode: string }[], rates: readonly { country: string; valid_from: string }[], today: string): string[] {
  const half = `${today.slice(0, 4)}-${today.slice(5, 7) < "07" ? "01" : "07"}-01`;
  const name: Record<string, string> = { DE: "Deutschland", AT: "Österreich", CH: "Schweiz" };
  const tag = (iso: string) => utcDate(`${iso}T12:00:00Z`);
  const out: string[] = [];
  for (const r of rules) {
    if (r.mode !== "base_plus") continue;
    const land = name[r.country] ?? r.country;
    const latest = rates.filter((x) => x.country === r.country).map((x) => x.valid_from.slice(0, 10)).sort().at(-1);
    if (!latest) out.push(`Basiszins fehlt: Für ${land} ist kein Satz hinterlegt; Agenturen mit Sitz dort rechnen keine Verzugszinsen. Bitte unter Produkt-Standards · Basiszins eintragen.`);
    else if (latest < half) out.push(`Basiszins veraltet: Der jüngste Satz für ${land} gilt ab ${tag(latest)}, fällig war ein Satz ab ${tag(half)}; bis dahin rechnen die Agenturen mit dem alten Satz weiter. Bitte unter Produkt-Standards · Basiszins eintragen.`);
  }
  return out;
}
